import { formatCount, formatMoney } from "../lib/money";
import { formatRate, linkMetrics } from "../lib/insights";
import { useTranslation } from "react-i18next";

/**
 * Clicks → signups → customers, and the two rates between them.
 *
 * The rates are the point, not the counts. "Four hundred clicks" says nothing
 * on its own; "four hundred clicks and two signups" says the traffic is wrong,
 * and "twenty clicks and eight signups" says find more of that. So each step
 * carries the proportion that survived it, and the bar widths are drawn against
 * the first step rather than each against the last — a funnel where every stage
 * is full is not a funnel.
 *
 * Drawn rather than charted: three divs with a percentage width, which is the
 * whole of it. `ClickChart` next door records what goes wrong with percentage
 * heights inside a flex row; widths have no such trap, because a block element
 * already resolves its width against its parent.
 *
 * ---------------------------------------------------------------------------
 * What the last step means, and what it does not
 * ---------------------------------------------------------------------------
 * "Customers" is a referral whose first top-up came through — the point a
 * commission is earned. It is deliberately not called "conversions" on screen:
 * the affiliate is looking at people, and every one of these is somebody who
 * paid Maxlayer money.
 *
 * Nobody is named at any stage. The API sends counts and nothing else, so this
 * is a property of the boundary rather than of the markup.
 */
export function Funnel({
  clicks,
  referrals,
  conversions,
  earnedMicros,
}: {
  clicks: number;
  referrals: number;
  conversions: number;
  earnedMicros: number;
}) {
  const { t } = useTranslation("charts");
  const metrics = linkMetrics({ clicks, referrals, conversions, earnedMicros });

  const steps = [
    {
      key: "clicks",
      label: t("funnel.clicks"),
      value: clicks,
      /* A floor of 2% so a stage with one survivor is visibly a stage with one
         survivor rather than an invisible sliver against a large first step. */
      width: clicks === 0 ? 0 : 100,
      rate: null as string | null,
      rateLabel: "",
      tone: "bg-fg-subtle/35",
    },
    {
      key: "signups",
      label: t("funnel.signups"),
      value: referrals,
      width: clicks === 0 ? 0 : Math.max(referrals > 0 ? 2 : 0, (referrals / clicks) * 100),
      rate: formatRate(metrics.signupRate),
      rateLabel: t("funnel.ofClicks"),
      tone: "bg-accent/70",
    },
    {
      key: "customers",
      label: t("funnel.customers"),
      value: conversions,
      width: clicks === 0 ? 0 : Math.max(conversions > 0 ? 2 : 0, (conversions / clicks) * 100),
      rate: formatRate(metrics.conversionRate),
      rateLabel: t("funnel.ofSignups"),
      tone: "bg-mint/70",
    },
  ];

  return (
    <div className="space-y-4 px-4 py-4">
      {steps.map((step) => (
        <div key={step.key} className="space-y-1.5">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <span className="text-[13px] text-fg">{step.label}</span>
            <span className="text-[13px] tabular-nums text-fg-muted">
              {formatCount(step.value)}
              {step.rate && (
                <span className="text-fg-subtle">
                  {" · "}
                  {step.rate} {step.rateLabel}
                </span>
              )}
            </span>
          </div>
          {/* A track behind every bar, so a stage that lost almost everybody
              still shows how much of the width it failed to fill. Without it a
              1% bar looks like a rendering fault. */}
          <div className="h-2 w-full overflow-hidden rounded-full bg-raised">
            <div
              className={`h-full rounded-full transition-[width] duration-500 ease-smooth ${step.tone}`}
              style={{ width: `${step.width}%` }}
            />
          </div>
        </div>
      ))}

      {/*
       * Earnings per click, under the funnel rather than in it, because it is
       * not a stage — it is what the whole funnel came to per visit, and it is
       * the number to compare one campaign against another with. Total earnings
       * reward whichever link has been up longest.
       */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-t border-line pt-3">
        <span className="text-[13px] text-fg">{t("funnel.perClick")}</span>
        <span className="text-[13px] tabular-nums text-fg">
          {Number.isFinite(metrics.epcMicros) ? formatMoney(metrics.epcMicros) : "—"}
          <span className="text-fg-subtle">
            {" · "}
            {formatMoney(earnedMicros)} in total
          </span>
        </span>
      </div>
    </div>
  );
}
